import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ─────────── Company profile ───────────
export type CompanyProfile = {
  legalName: string;
  dba?: string;
  ein?: string;
  fscCertificate?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone: string;
  email: string;
  website?: string;
  officeHours?: string;
  shippingHours?: string;
  fiscalYearStart: string; // 'MM-DD'
  defaultCurrency: string;
  taxId?: string;
};

export function useCompanyProfile() {
  return useQuery({
    queryKey: ['settings', 'company'],
    queryFn: async () => {
      const { data } = await api.get<CompanyProfile>('/settings/company');
      return data;
    },
  });
}

export function useSaveCompanyProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CompanyProfile) => {
      const { data } = await api.put('/settings/company', input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'company'] }),
  });
}

// ─────────── Integrations status ───────────
export type IntegrationStatus = {
  ses: { configured: boolean; from?: string; sandbox?: boolean };
  s3:  { configured: boolean; bucket?: string };
  appCheck: { enabled: boolean };
  quickbooks?: { connected: boolean };
};

export function useIntegrationStatus() {
  return useQuery({
    queryKey: ['settings', 'integrations'],
    queryFn: async () => {
      const { data } = await api.get<IntegrationStatus>('/settings/integrations');
      return data;
    },
  });
}

export function useSendTestEmail() {
  return useMutation({
    mutationFn: async (to: string) => {
      const { data } = await api.post('/settings/integrations/ses/test', { to });
      return data as { messageId: string };
    },
  });
}

// ─────────── Audit log ───────────
export type AuditEntry = {
  id: string;
  actorUid: string;
  actorEmail: string;
  actorRole: string;
  action: string;
  resourceType: string;
  resourceId: string | null;
  metadata: Record<string, unknown>;
  timestamp: string;
};

export function useAuditLog(filters: { limit?: number; action?: string } = {}) {
  return useQuery({
    queryKey: ['audit', filters],
    queryFn: async () => {
      const { data } = await api.get<{ items: AuditEntry[] }>('/audit', { params: filters });
      return data.items;
    },
  });
}

// ─────────── User preferences (per-user) ───────────
export type UserPreferences = {
  density: 'comfortable' | 'compact';
  emailDigest: 'off' | 'daily' | 'weekly';
  notifyOnOrders: boolean;
  notifyOnLowStock: boolean;
};

export function useMyPreferences() {
  return useQuery({
    queryKey: ['settings', 'preferences'],
    queryFn: async () => {
      const { data } = await api.get<UserPreferences>('/settings/preferences');
      return data;
    },
  });
}

export function useSaveMyPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (prefs: UserPreferences) => {
      const { data } = await api.put('/settings/preferences', prefs);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'preferences'] }),
  });
}
